"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { FreeDigitalReportHeroButton } from "@/components/shared/DigitalReportLeadProvider";

export function HeroSection() {
  const scope = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const overlay = overlayRef.current;
      const main = mainRef.current;
      if (!overlay || !main) return;

      gsap.set(overlay, { opacity: 0 });
      gsap.set(main, { autoAlpha: 0 });

      const kids = main.querySelectorAll<HTMLElement>("[data-hero-reveal]");

      const tl = gsap.timeline();
      tl.to(overlay, {
        opacity: 0.7,
        duration: 1.1,
        ease: "power2.inOut",
      })
        .to(main, { autoAlpha: 1, duration: 0.35, ease: "power2.out" }, "-=0.45")
        .from(
          kids,
          {
            autoAlpha: 0,
            y: 28,
            stagger: 0.1,
            duration: 0.75,
            ease: "power3.out",
          },
          "-=0.15",
        );
    },
    { scope },
  );

  return (
    <section
      ref={scope}
      className="relative flex min-h-dvh flex-col overflow-x-hidden font-sans text-white"
    >
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          playsInline
          preload="auto"
        >
          <source src="/images/main_comp_super_fast.mp4" type="video/mp4" />
        </video>
        <div ref={overlayRef} className="absolute inset-0 bg-black" />
      </div>

      <div className="relative z-10 flex min-h-dvh flex-col overflow-hidden pt-20 md:h-dvh md:pt-20">
        <div
          ref={mainRef}
          className="invisible flex h-full flex-1 items-end py-8 opacity-0 md:items-center md:py-16"
        >
          <div className="site-content h-full">
            <div className="grid h-full grid-cols-1 gap-6 md:gap-6 lg:grid-cols-12 lg:gap-10">
              <div className="self-start max-w-full lg:col-span-8 lg:mt-20 lg:max-w-[850px]">
                <h1
                  data-hero-reveal
                  className="text-sm font-medium tracking-tight text-white/85 md:text-base"
                >
                  Web design nelson
                </h1>
                <h2
                  data-hero-reveal
                  className="mt-4 text-[56px] leading-[0.9] tracking-tight text-white sm:mt-5 sm:text-7xl lg:text-8xl xl:text-[150px]"
                >
                  <span className="text-[#ff5c00]">shopify </span>
                  design and development
                </h2>
                <span data-hero-reveal className="mt-10 block w-max md:mt-12">
                  <FreeDigitalReportHeroButton />
                </span>
              </div>

              <div className="flex flex-col justify-center lg:col-span-4">
                <p
                  data-hero-reveal
                  className="text-lg font-medium leading-snug text-[#ff5c00] md:text-3xl"
                >
                  <span className="mr-1 hidden text-4xl text-[#ff5c00] md:block">*</span>
                  We work with brands to create
                  solutions that drive scale and
                  results through innovation
                </p>
                <p
                  data-hero-reveal
                  className="mt-4 text-base leading-relaxed text-white/90 md:mt-6 md:text-lg"
                >
                  We design Shopify and e-commerce website
                  solutions that don’t just look good, they actually do
                  their job. We blend strategy, UX, and sharp creativity
                  to remove friction, build trust, and guide customers
                  smoothly to checkout. No confusion, no rage-clicking,
                  no abandoned carts, just websites that convert
                  browsers into buyers and quietly outperform expectations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
